/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { CarouselSlider } from '@/components/blocks/Carousel';
import { StaticSlider } from '@/components/blocks/Carousel/StaticSlider';
import { carouselSlideSX } from '@/components/blocks/Carousel/styles/carouselSlide';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { RenderContent } from '@/components/blocks/RenderContent';
import { CategoryCard } from '@/components/content/CategoryCard';
import { useCategoryRecommendation } from '@/data/Content/CategoryRecommendation';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { useUser } from '@/data/User';
import { DEFAULT_CAROUSEL_OPTS } from '@/data/constants/carousel';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { CarouselOptions } from '@/data/types/Carousel';
import { CategoryType } from '@/data/types/Category';
import { WidgetComponentProps, WidgetProperties } from '@/data/types/Slot';
import { getContractIdFromContext } from '@/utils/getContractIdFromContext';
import { useMediaQuery, useTheme } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';

const emptyProperties = {} as WidgetProperties;

const cardContainerMapper = (
	events: any[],
	contract: string,
	clickAction: (category: string) => void
) =>
	events.map(({ id }) => ({
		id,
		card: (
			<CategoryCard key={`${contract}_${id}`} id={id} clickAction={clickAction.bind(null, id)} />
		),
		a11y: { 'aria-label': undefined, 'aria-labelledby': id.toString() },
	}));

type CategoryRecommendationProps = WidgetComponentProps & {
	/**
	 * Do not apply any replacements to the content (See `parseHTMLValidate`).
	 */
	skipParsing?: boolean;
};

export const CategoryRecommendation: FC<CategoryRecommendationProps> = ({
	id: _id,
	properties = emptyProperties,
	skipParsing,
}) => {
	const { emsName = '' } = properties;
	const { categories: events, clickAction, loading, title } = useCategoryRecommendation(emsName);
	const { onContentClick } = useContentEvents();
	const { user } = useUser();
	const [contract, setContract] = useState<string>(getContractIdFromContext(user?.context));
	const [_invalid, setInvalid] = useState<Record<string, boolean>>({});

	const originalCategoriesArr = useMemo(
		() => cardContainerMapper(events, contract, clickAction),
		[events, contract, clickAction]
	);

	const { slides, a11yProps } = useMemo(() => {
		const slides = originalCategoriesArr.map(({ card }) => card);
		const a11yProps = originalCategoriesArr.map(({ a11y }) => a11y);
		return { slides, a11yProps };
	}, [originalCategoriesArr]);

	const theme = useTheme();
	const lgMatch = useMediaQuery(theme.breakpoints.down('lg'));
	const mdMatch = useMediaQuery(theme.breakpoints.down('md'));
	const smMatch = useMediaQuery(theme.breakpoints.down('sm'));
	const visibleSlides = useMemo(
		() => (smMatch ? 1 : mdMatch ? 2 : lgMatch ? 3 : 4),
		[lgMatch, mdMatch, smMatch]
	);

	const disabledSliding = useMemo(() => visibleSlides >= slides.length, [visibleSlides, slides]);

	const carouselProps: CarouselOptions = useMemo(
		() => ({
			...DEFAULT_CAROUSEL_OPTS,
			visibleSlides,
			step: visibleSlides,
			totalSlides: slides.length,
		}),
		[slides.length, visibleSlides]
	);

	const onNotify = (id: string, usedContract: string, category: CategoryType) =>
		setInvalid((old) => ({ ...old, [`${id}_${usedContract}`]: !category }));
	useEffect(() => {
		// we only need to trigger updates on changes, not initial loads -- initial load updates are
		//   taken care of by the initial render's invocations of `onNotify`
		const _contract = getContractIdFromContext(user?.context);
		if (contract !== _contract) {
			setContract(_contract);
		}
	}, [user?.context]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<ContentProvider value={{ onNotify }}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
					skipParsing={skipParsing}
				/>
			))}
			:{' '}
			{loading ? (
				<ProgressIndicator />
			) : disabledSliding ? (
				<StaticSlider>{slides}</StaticSlider>
			) : (
				<CarouselSlider
					a11yProps={a11yProps}
					slides={slides}
					carouselProps={carouselProps}
					carouselSlideStyles={carouselSlideSX}
				/>
			)}
		</ContentProvider>
	);
};
